/**
 * useTagManagement Hook
 * 
 * Hook для управления состоянием тегов в диалоге управления тегами
 * Инкапсулирует локальное состояние, поиск и сохранение тегов
 */

import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import tagService from '@/services/tagService';
import type { Tag } from '@/integrations/supabase/types';

interface TagManagementState {
  selectedTags: Tag[];
  searchQuery: string;
  searchResults: Tag[];
  isCreating: boolean;
  hasUnsavedChanges: boolean;
}

interface UseTagManagementReturn extends TagManagementState {
  addTag: (tag: Tag) => void;
  removeTag: (tagId: string) => void;
  setSearchQuery: (query: string) => void;
  searchTags: (query: string) => void;
  resetToInitial: () => void;
  saveChanges: () => Promise<void>;
  isSaving: boolean;
  updateTagInSelected: (updatedTag: Tag) => void;
  removeTagFromSelected: (tagId: string) => void;
}

// Debounce utility function
const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const useTagManagement = (
  recordId: string,
  userId: string,
  initialTags: Tag[]
): UseTagManagementReturn => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Локальное состояние
  const [state, setState] = useState<TagManagementState>({
    selectedTags: [...initialTags],
    searchQuery: '',
    searchResults: [],
    isCreating: false,
    hasUnsavedChanges: false
  });

  // Мутация для сохранения тегов
  const updateRecordTagsMutation = useMutation({
    mutationFn: async (tags: Tag[]) => {
      const tagIds = tags.map(t => t.id);
      return await tagService.updateRecordTags(recordId, tagIds);
    },
    onMutate: async (newTags) => {
      // Отменяем исходящие запросы для записи
      await queryClient.cancelQueries({ queryKey: ['record', recordId] });
      
      // Получаем предыдущие данные для отката
      const previousRecord = queryClient.getQueryData(['record', recordId]);
      
      // Оптимистично обновляем кэш
      queryClient.setQueryData(['record', recordId], (oldData: unknown) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          tags: newTags
        };
      });

      return { previousRecord };
    },
    onSuccess: () => {
      // Инвалидируем связанные запросы
      queryClient.invalidateQueries({ queryKey: ['records'] });
      queryClient.invalidateQueries({ queryKey: ['records-infinite'] });
      queryClient.invalidateQueries({ queryKey: ['userTags'] });
      
      // Сбрасываем флаг несохраненных изменений
      setState(prev => ({ 
        ...prev, 
        hasUnsavedChanges: false 
      }));
      
      toast({
        title: "Успешно",
        description: "Теги записи обновлены",
      });
    },
    onError: (error, variables, context) => {
      // Откатываем оптимистичные изменения
      if (context?.previousRecord) {
        queryClient.setQueryData(['record', recordId], context.previousRecord);
      }
      
      console.error('Error updating tags:', error);
      toast({
        title: "Ошибка",
        description: "Не удалось обновить теги",
        variant: "destructive",
      });
    }
  });

  // Debounced поиск тегов
  const debouncedSearchTags = useCallback(
    debounce(async (query: string) => {
      if (!query.trim()) {
        setState(prev => ({ ...prev, searchResults: [] }));
        return;
      }
      
      try {
        const results = await tagService.searchTags(userId, query);
        setState(prev => ({ ...prev, searchResults: results }));
      } catch (error) {
        console.error('Error searching tags:', error);
        setState(prev => ({ ...prev, searchResults: [] }));
      }
    }, 300),
    [userId]
  );

  // Функции управления тегами
  const addTag = useCallback((tag: Tag) => {
    setState(prev => {
      // Проверяем, не добавлен ли тег уже
      const isAlreadySelected = prev.selectedTags.some(t => t.id === tag.id);
      if (isAlreadySelected) {
        return prev;
      }

      return {
        ...prev,
        selectedTags: [...prev.selectedTags, tag],
        hasUnsavedChanges: true
      };
    });
  }, []);

  const removeTag = useCallback((tagId: string) => {
    setState(prev => {
      const newSelectedTags = prev.selectedTags.filter(t => t.id !== tagId);
      
      // Если тег не был найден, не изменяем состояние
      if (newSelectedTags.length === prev.selectedTags.length) {
        return prev;
      }

      return {
        ...prev,
        selectedTags: newSelectedTags,
        hasUnsavedChanges: true
      };
    });
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    setState(prev => ({ ...prev, searchQuery: query }));
  }, []);

  const searchTags = useCallback((query: string) => {
    debouncedSearchTags(query);
  }, [debouncedSearchTags]);

  const resetToInitial = useCallback(() => {
    setState({
      selectedTags: [...initialTags],
      searchQuery: '',
      searchResults: [],
      isCreating: false,
      hasUnsavedChanges: false
    });
  }, [initialTags]);

  const saveChanges = useCallback(async (): Promise<void> => {
    if (!state.hasUnsavedChanges) {
      return;
    }
    
    await updateRecordTagsMutation.mutateAsync(state.selectedTags);
  }, [state.hasUnsavedChanges, state.selectedTags, updateRecordTagsMutation]);

  // Функция для обновления тега в selectedTags после редактирования
  const updateTagInSelected = useCallback((updatedTag: Tag) => {
    setState(prev => {
      const tagIndex = prev.selectedTags.findIndex(t => t.id === updatedTag.id);
      if (tagIndex === -1) {
        // Тег не был выбран, просто возвращаем предыдущее состояние
        return prev;
      }

      const newSelectedTags = [...prev.selectedTags];
      newSelectedTags[tagIndex] = updatedTag;
      
      return {
        ...prev,
        selectedTags: newSelectedTags,
        hasUnsavedChanges: true
      };
    });
  }, []);

  // Функция для удаления тега из selectedTags после удаления тега
  const removeTagFromSelected = useCallback((tagId: string) => {
    setState(prev => {
      const newSelectedTags = prev.selectedTags.filter(t => t.id !== tagId);
      
      // Если тег не был найден, не изменяем состояние
      if (newSelectedTags.length === prev.selectedTags.length) {
        return prev;
      }

      return {
        ...prev,
        selectedTags: newSelectedTags,
        hasUnsavedChanges: true
      };
    });
  }, []);

  return {
    ...state,
    addTag,
    removeTag,
    setSearchQuery,
    searchTags,
    resetToInitial,
    saveChanges,
    isSaving: updateRecordTagsMutation.isPending,
    updateTagInSelected,
    removeTagFromSelected
  };
};